import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { DeliveryQueueJobSummary } from '../services/firebaseFunctions';

interface DeliveryQueueActiveJobsProps {
  jobs: DeliveryQueueJobSummary[];
  loading: boolean;
  error: string | null;
  cancelError: string | null;
  cancellingJobId: string | null;
  canManage: boolean;
  onCancel: (jobId: string, reason?: string) => void | Promise<void>;
  resolveCompanyName: (companyId?: string) => string;
  resolveProductName: (productId?: string) => string;
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DeliveryQueueActiveJobs: React.FC<DeliveryQueueActiveJobsProps> = ({
  jobs,
  loading,
  error,
  cancelError,
  cancellingJobId,
  canManage,
  onCancel,
  resolveCompanyName,
  resolveProductName,
}) => {
  const [cancelTarget, setCancelTarget] = useState<DeliveryQueueJobSummary | null>(null);
  const [reason, setReason] = useState('');

  const closeDialog = () => {
    setCancelTarget(null);
    setReason('');
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    await onCancel(cancelTarget.jobId, reason);
    closeDialog();
  };

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Processamento de entregas em andamento
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {cancelError && <Alert severity="error" sx={{ mb: 1 }}>{cancelError}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Empresa</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell align="center">Progresso</TableCell>
              <TableCell>Iniciado em</TableCell>
              <TableCell align="right">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.jobId} hover>
                <TableCell>{resolveCompanyName(job.filters.companyId)}</TableCell>
                <TableCell>{resolveProductName(job.filters.productId)}</TableCell>
                <TableCell align="center">
                  {job.processedItems} / {job.totalItems}
                </TableCell>
                <TableCell>{formatDate(job.createdAt)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Entregas Pendentes.'}>
                    <span>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={!canManage || cancellingJobId === job.jobId}
                        onClick={() => setCancelTarget(job)}
                      >
                        Cancelar
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(cancelTarget)} onClose={closeDialog}>
        <DialogTitle>Cancelar processamento de entregas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Esta ação encerra o processamento de entregas como cancelado. Nenhum novo lote será processado.
          </Typography>
          <TextField
            label="Motivo (opcional)"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Voltar</Button>
          <Button color="error" variant="contained" onClick={confirmCancel}>
            Confirmar cancelamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryQueueActiveJobs;
